import { timestampFromDate } from '@bufbuild/protobuf/wkt';
import { create } from '@bufbuild/protobuf';
import { queryOptions } from '@tanstack/react-query';
import {
  ListMetricsRequest_AggregationType,
  ListMetricsRequest_TargetSchema,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/aqora_service_pb';
import { MetricType } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/metrics_pb';
import { SimulationModel } from '~/shared/models/system/SimulationModel';
import { listMetrics } from '~/services/api/fetchers/metrics.api';
import {
  DateRange,
  getDefaultDateRange,
} from '~/shared/models/metrics/date-range';
import { getLastWeekRange } from '~/shared/api/metrics.queries';
import { KpiListModel } from '~/pages/Dashboard/models/KpiListModel';
import { WaterStoredKpiModel } from '~/pages/Dashboard/models/WaterStoredKpiModel';
import {
  listKpisDashboard,
  listSimulationKpisDashboard,
} from '~/services/api/fetchers/kpis.api';

interface ListRainwaterKpisOptions {
  systemName: string;
  dateRange?: DateRange;
  simulation?: SimulationModel;
  kpiMetricTypes: MetricType[];
}

export const listKpis = ({
  systemName,
  dateRange = getDefaultDateRange(),
  simulation,
  kpiMetricTypes,
}: ListRainwaterKpisOptions) => {
  const [fromDate, endDate] = dateRange;

  return queryOptions({
    queryKey: [
      'listKpis',
      systemName,
      fromDate,
      endDate,
      kpiMetricTypes,
      simulation?.id,
    ],
    queryFn: async () => {
      const listMetricsResponse = simulation
        ? await listSimulationKpisDashboard({
            simulationId: simulation.id,
            resourceName: systemName,
            rangeFrom: timestampFromDate(fromDate),
            rangeTo: timestampFromDate(endDate),
          })
        : await listKpisDashboard({
            resourceName: systemName,
            rangeFrom: timestampFromDate(fromDate),
            rangeTo: timestampFromDate(endDate),
          });
      return new KpiListModel(listMetricsResponse, kpiMetricTypes);
    },
  });
};

export const getWaterStoredKpiQuery = ({
  systemName,
}: {
  systemName: string;
}) =>
  queryOptions({
    queryKey: ['getWaterStoredKpi', { systemName }],
    queryFn: async () => {
      const targets = [
        create(ListMetricsRequest_TargetSchema, {
          resourceName: systemName,
          types: [MetricType.KPI_V2_WATER_STORED_L],
        }),
      ];

      const listMetricsResponse = await listMetrics({
        targets,
        aggregation: ListMetricsRequest_AggregationType.MOST_RECENT_ONLY,
        rangeFrom: getLastWeekRange().rangeFrom,
        rangeTo: getLastWeekRange().rangeTo,
      });

      return new WaterStoredKpiModel(listMetricsResponse);
    },
  });
